/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

const selectSavedSearchesCrudOperation = (state) => state.savedSearches.crudOperation;
const selectSavedSearchesCrudStatus = (state) => state.savedSearches.crudStatus;
const selectSavedSearchesInitialLoad = (state) => state.savedSearches.loading;
const selectSavedSearchesError = (state) => state.savedSearches.error;
const selectActiveSavedSearch = (state) => state.savedSearches.activeSavedSearch;

const selectSavedSearches = createSelector(
  (state) => state.savedSearches, (slice) => slice.savedSearchList,
);
const selectSavedSearchById = createSelector(
  [selectSavedSearches,
    (searchList, id) => id],
  (searchList, searchId) => searchList.find((search) => search.id === searchId),
);

const fetchSavedSearch = createAsyncThunk(
  'securedApi/savedSearches/fetchSaved',
  async (id, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/saved-search/${id}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const fetchSavedSearches = createAsyncThunk(
  'securedApi/savedSearches/fetchSavedSearches',
  async (searchType, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/saved-search/?typeId=${searchType}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const editSavedSearch = createAsyncThunk(
  'securedApi/savedSearches/editSavedSearch',
  async (editSearchData, thunkAPI) => {
    const { savedSearch, patchFields } = editSearchData;
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .patch(`${envConfig.apiUrl}/api/v1/saved-search/${savedSearch.id}`,
          { data: { ...savedSearch, patchFields: [...patchFields] } });
      return thunkAPI.fulfillWithValue({ ...savedSearch });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const createSavedSearch = createAsyncThunk(
  'securedApi/savedSearches/createSavedSearch',
  async (savedSearch, thunkAPI) => {
    try {
      const { id } = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/saved-search/`,
          { data: { ...savedSearch } });
      return thunkAPI.fulfillWithValue({ ...savedSearch, id });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const deleteSavedSearch = createAsyncThunk(
  'securedApi/savedSearches/deleteSavedSearch',
  async (id, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/saved-search/${id}`);
      return thunkAPI.fulfillWithValue(id);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  savedSearchList: [],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: false,
  loading: true,
  activeSavedSearch: null,
  isLocalStorageLoaded: false,
};

export const savedSearchesSlice = createSlice({
  name: 'savedSearches',
  initialState,
  reducers: {
    clearSavedSearchesSlice: () => initialState,
    setActiveSavedSearch: (state, action) => {
      state.activeSavedSearch = action.payload;
    },
    clearActiveSearch: (state) => {
      state.activeSavedSearch = null;
    },
    removeSavedSearch: (state, action) => {
      state.savedSearchList = [...state.savedSearchList]
        .filter((search) => search.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedSearch.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchSavedSearch.fulfilled, (state, action) => {
        const { savedSearch } = action.payload;
        const foundIndex = state.savedSearchList
          .findIndex((range) => range.id === savedSearch.id);
        if (foundIndex !== -1) {
          state.savedSearchList.splice(foundIndex, 1, savedSearch);
        } else {
          state.savedSearchList.push(savedSearch);
        }
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(fetchSavedSearch.rejected, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(fetchSavedSearches.pending, (state) => {
        state.loading = true;
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchSavedSearches.fulfilled, (state, action) => {
        const { savedSearches } = action.payload;
        state.savedSearchList = savedSearches;
        state.crudStatus = CrudStatus.SUCCESS;
        state.loading = false;
        state.error = false;
      })
      .addCase(fetchSavedSearches.rejected, (state) => {
        state.error = true;
        state.loading = false;
        state.crudStatus = CrudStatus.ERROR;
      })
      .addCase(editSavedSearch.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(editSavedSearch.fulfilled, (state, action) => {
        const newSavedSearch = action.payload;
        const foundIndex = state.savedSearchList
          .findIndex((range) => range.id === newSavedSearch.id);
        if (foundIndex !== -1) {
          state.savedSearchList.splice(foundIndex, 1, newSavedSearch);
        } else {
          state.savedSearchList.push(newSavedSearch);
        }
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(editSavedSearch.rejected, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(createSavedSearch.pending, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(createSavedSearch.fulfilled, (state, action) => {
        const newSavedSearch = action.payload;
        const foundIndex = state.savedSearchList
          .findIndex((range) => range.id === newSavedSearch.id);
        if (foundIndex !== -1) {
          state.savedSearchList.splice(foundIndex, 1, newSavedSearch);
        } else {
          state.savedSearchList.push(newSavedSearch);
        }
        state.activeSavedSearch = newSavedSearch;
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(createSavedSearch.rejected, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(deleteSavedSearch.pending, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(deleteSavedSearch.fulfilled, (state, action) => {
        state.savedSearchList = [...state.savedSearchList]
          .filter((search) => search.id !== action.payload);
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(deleteSavedSearch.rejected, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      });
  },
});

const { actions, reducer } = savedSearchesSlice;
export const {
  clearSavedSearchesSlice, removeSavedSearch, setActiveSavedSearch, clearActiveSearch,
} = actions;
export {
  selectSavedSearches,
  fetchSavedSearches,
  selectSavedSearchById,
  editSavedSearch,
  createSavedSearch,
  deleteSavedSearch,
  selectSavedSearchesCrudOperation,
  selectSavedSearchesInitialLoad,
  selectSavedSearchesError,
  selectSavedSearchesCrudStatus,
  selectActiveSavedSearch,
  fetchSavedSearch,
};

export default reducer;
